import { createGlobalState } from '@vueuse/core';
import type { ShownInterviewInvite } from '~/types/interview-invite.types';
import { InterviewInviteOutcome } from '~/enums/interview-invite.enums';
import { InterviewInviteEvents } from '~/enums/tracking/tracking-events.enums';
import {
  INTERVIEW_INVITE_FEATURE_OFF,
  INTERVIEW_INVITE_GONE,
  useInterviewInvite,
} from '~/composables/feedbackPrompts/useInterviewInvite';

const LESSON_OR_QUIZ_PATH = /^\/courses\/[^/]+\/(lessons|quizzes)\//;

function stripLocalePrefix (path: string): string {
  return path.replace(/^\/(en|de|fr|es)(?=\/|$)/, '');
}

function isLessonOrQuizPath (path: string): boolean {
  return LESSON_OR_QUIZ_PATH.test(stripLocalePrefix(path));
}

export const useInterviewInviteStore = createGlobalState(() => {
  const { fetchInvite, recordOutcome: postOutcome } = useInterviewInvite();

  const state = reactive<{
    invite: ShownInterviewInvite | null;
    hasCheckedThisLoad: boolean;
    isDialogVisible: boolean;
    shownInviteId: number | null;
  }>({
    invite: null,
    hasCheckedThisLoad: false,
    isDialogVisible: false,
    shownInviteId: null,
  });

  const invite = computed(() => state.invite);
  const isDialogVisible = computed(() => state.isDialogVisible);

  const toastSummary = (key: string): string =>
    useNuxtApp().$i18n?.t(`interviewInvite.errors.${key}`) ?? `interviewInvite.errors.${key}`;

  const track = (event: InterviewInviteEvents, current: ShownInterviewInvite): void => {
    useTracking().dispatchTrackingEvent(event, {
      trigger: current.triggerType,
      platform: 'web',
      invite_id: current.inviteId,
    });
  };

  const revealIfAllowed = (path: string): void => {
    if (!state.invite || state.isDialogVisible || isLessonOrQuizPath(path)) {
      return;
    }
    state.isDialogVisible = true;

    if (state.shownInviteId !== state.invite.inviteId) {
      state.shownInviteId = state.invite.inviteId;
      track(InterviewInviteEvents.Shown, state.invite);
    }
  };

  let checkRequest: Promise<void> | null = null;
  let checkGeneration = 0;

  const checkOnAppOpen = (getPath: () => string): Promise<void> => {
    if (checkRequest) {
      return checkRequest;
    }
    if (state.hasCheckedThisLoad || !useUserStore().user.value || stripLocalePrefix(getPath()).startsWith('/auth/')) {
      return Promise.resolve();
    }

    state.hasCheckedThisLoad = true;
    const generation = ++checkGeneration;
    checkRequest = (async () => {
      try {
        const result = await fetchInvite();

        if (generation !== checkGeneration) {
          return;
        }

        if (result === INTERVIEW_INVITE_FEATURE_OFF) {
          return;
        }

        if (result.showInvite && result.inviteId !== null && result.triggerType !== null && result.schedulerUrl) {
          state.invite = {
            inviteId: result.inviteId,
            triggerType: result.triggerType,
            schedulerUrl: result.schedulerUrl,
          };
          revealIfAllowed(getPath());
        }
      } catch (error) {
        if (generation !== checkGeneration) {
          return;
        }
        useNuxtApp().$sentry?.captureException(error);
      } finally {
        if (generation === checkGeneration) {
          checkRequest = null;
        }
      }
    })();
    return checkRequest;
  };

  const recordOutcome = async (outcome: InterviewInviteOutcome): Promise<void> => {
    const current = state.invite;
    if (!current) {
      return;
    }

    state.isDialogVisible = false;

    const result = await useErrorHandling(
      () => postOutcome(current.inviteId, outcome),
      toastSummary('recordOutcomeTitle'),
      toastSummary('recordOutcomeFailed'),
    );

    if (result === undefined) {
      if (outcome === InterviewInviteOutcome.Responded) {
        state.invite = null;
      }
      return;
    }

    state.invite = null;

    if (result === INTERVIEW_INVITE_GONE) {
      return;
    }

    track(outcome === InterviewInviteOutcome.Responded ? InterviewInviteEvents.Accepted : InterviewInviteEvents.Declined, current);
  };

  const respond = (): Promise<void> => recordOutcome(InterviewInviteOutcome.Responded);
  const dismiss = (): Promise<void> => recordOutcome(InterviewInviteOutcome.Dismissed);

  const close = (): void => {
    state.isDialogVisible = false;
    state.invite = null;
  };

  const reset = (): void => {
    state.invite = null;
    state.hasCheckedThisLoad = false;
    state.isDialogVisible = false;
    state.shownInviteId = null;
    checkRequest = null;
    checkGeneration++;
  };

  return {
    invite,
    isDialogVisible,
    checkOnAppOpen,
    revealIfAllowed,
    respond,
    dismiss,
    close,
    reset,
  };
});
